import { multiStep } from 'components/Wizards/MultiStepWizard/multiStep'
import Styles from './AddPhoto.module.scss'
import { AddPhotoProps } from './Types'
import { Formik } from 'formik'
import { Form } from 'components/Wizards/Form/Form'
import { ImagePickerProvider, useImagePickerContext } from 'components/ImagePickerModal/ImagePickerContext'
import { useImportMediaFiles } from 'hooks/useImportMediaFiles'
import { SelectedImage } from 'types'
import { useUploadsProject } from 'hooks/useUploadsProject'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useConfirm } from './useConfirm'
import { ResponsiveDesign } from 'screens/blaze/Home/components/ResponsiveDesign'
import { Provider } from 'components/ImagePickerModal/Types'
import { StepSchema } from 'components/Wizards/schemas/Types'
import { Button } from 'blaze-ui/Button'
import { ArrowLeft } from 'blaze-ui/icons'
import cx from 'classnames'
import { useEffect, useRef, useState } from 'react'
import { LoadingDesign } from './LoadingDesign'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { useImages } from 'components/Wizards/SelectImages/ImagesProvider'
import { MobileSelectImage } from './MobileSelectImage/MobileSelectImage'
import { SelectImage } from './SelectImage/SelectImage'

const getSubmitLabel = (schema: StepSchema, provider?: Provider) => {
  if (provider === 'ai') {
    return 'Use Generated Image'
  }

  return schema.describe().meta.submitLabel
}

interface Values {
  source_visual_element_id?: number
}

const Content = (props: AddPhotoProps) => {
  const { schema, initialValues, onSubmit, isLoading } = props
  const { selectedImages, provider, setProvider, isAIGenerating } = useImagePickerContext()
  const { confirm } = useConfirm()
  const { isMobile } = useResponsiveDesign()
  const [showLoading, setShowLoading] = useState(false)
  const resolveRef = useRef<(value: boolean | PromiseLike<boolean>) => void>()

  const showButtons = ['ai', 'stock', 'link'].includes(provider || '')

  return (
    <Formik
      enableReinitialize
      validationSchema={schema}
      onSubmit={async (values) => {
        setShowLoading(true)

        if (isMobile && !!resolveRef.current) {
          resolveRef.current?.(true)
        } else {
          // In the design wizards, a stock image can be selected outside of the 'stock' provider
          const isUnsplashedImage = !!selectedImages[0]?.unsplash
          await confirm()

          // For uploads via a provider other than 'uploads', the submission is handled on the provider's 'confirm' hook
          if (provider === 'uploads' && !isUnsplashedImage) await onSubmit(values)
        }
      }}
      initialValues={schema.cast({ ...schema.getDefault(), ...initialValues }, { assert: false })}
    >
      {({ isSubmitting, values }) => {
        const fields = values as Values

        return (
          <Form.Root>
            <Form.Section>
              <div
                className={cx(Styles.container, {
                  [Styles.fullHeight]: provider === 'stock',
                  [Styles.loadingDesign]: showLoading,
                })}
              >
                {showLoading && <LoadingDesign templateId={fields.source_visual_element_id} />}
                {!showLoading && (
                  <>
                    <ResponsiveDesign.Mobile>
                      <MobileSelectImage resolveRef={resolveRef} onSubmit={onSubmit} />
                    </ResponsiveDesign.Mobile>
                    <ResponsiveDesign.Desktop>
                      <SelectImage />
                    </ResponsiveDesign.Desktop>
                  </>
                )}
              </div>
            </Form.Section>

            {!showLoading && (
              <Form.Actions isSticky>
                {showButtons ? (
                  <Form.PreviousButton onPress={() => setProvider('uploads')}>Image Options</Form.PreviousButton>
                ) : (
                  <Button
                    variant="tertiary"
                    onPress={props.onBack}
                    isDisabled={isSubmitting || isLoading}
                    frontIcon={ArrowLeft}
                    size="lg"
                  >
                    {isMobile ? '' : 'Choose Template'}
                  </Button>
                )}
                <Form.PrimaryActions>
                  <Form.SubmitButton isDisabled={isSubmitting || isAIGenerating || isLoading} variant="primary">
                    {getSubmitLabel(schema, provider)}
                  </Form.SubmitButton>
                </Form.PrimaryActions>
              </Form.Actions>
            )}
          </Form.Root>
        )
      }}
    </Formik>
  )
}

const AddPhoto = multiStep<AddPhotoProps>((props) => {
  const { uploadImageFilesWithoutLoadingToast } = useImportMediaFiles(false)
  const workspaceId = useCurrentWorkspaceId()
  const uploadsProjectId = useUploadsProject(workspaceId).data?.project?.id
  const { generateImageKeywords, imageKeywords, topic: previousTopic } = useImages()

  const topic = props.initialValues.topic

  useEffect(() => {
    if (topic && topic !== previousTopic) generateImageKeywords({ topic })
  }, [generateImageKeywords, previousTopic, props.initialValues.topic, topic])

  return (
    <ImagePickerProvider
      defaultProvider="uploads"
      maxCount={1}
      uploadImages={async (files) => {
        let uploadedImages
        uploadedImages = await uploadImageFilesWithoutLoadingToast(files, uploadsProjectId)

        if (!uploadedImages) return []
        return uploadedImages.map((file) => ({ id: file.id, url: file.source_url } as SelectedImage))
      }}
      initialQuery={imageKeywords.join(' ') || ''}
      onConfirm={async (images) => {
        const image = images[0]

        if (!image?.id) return

        props.onSubmit({
          initial_media_file_id: image.id,
        })
      }}
      hiddenProviders={new Set<Provider>(['gifs'])}
    >
      <Content
        {...props}
        onSubmit={(values) => {
          return props.onSubmit({ initial_media_file_id: values.initial_media_file_id })
        }}
      />
    </ImagePickerProvider>
  )
})

export { AddPhoto }
